import { useEffect, useState, useCallback } from "react";
import { DataTable, ColumnDef, FilterOption } from "@/components/DataTable";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthContext";
import { Plus, Download } from "lucide-react";
// import { api, ApiClientError } from "@/lib/api"; // Uncomment when backend is ready

// Define the HostingProvider type
interface HostingProvider {
  id: string;
  provider_name: string;
  ncii_instances: number;
  type: string;
  region: string;
  active_urls: number;
  removed_urls: number;
  takedown_rate: number; // Percentage as decimal (e.g., 0.85 for 85%)
  avg_response_time: number; // In days
  contacts: string[]; // Array of email addresses
}

const PAGE_SIZE = 50;

export function HostingProviderList() {
  const { logout } = useAuth();
  const [providers, setProviders] = useState<HostingProvider[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [_totalCount, setTotalCount] = useState<number>(0);

  const loadProviders = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // TODO: Replace with actual API call
      // const data = await api.hostingProviders.list(PAGE_SIZE, currentPage);
      // setProviders(data);
      // setHasMore(data.length === PAGE_SIZE);
      // setTotalCount(data.totalCount); // Assuming API returns total count

      // Mock data for now
      const mockProviders: HostingProvider[] = [
        {
          id: "1",
          provider_name: "DigitalOcean",
          ncii_instances: 456,
          type: "Cloud VPS",
          region: "Global",
          active_urls: 89,
          removed_urls: 367,
          takedown_rate: 0.81,
          avg_response_time: 3.2,
          contacts: ["abuse@digitalocean.com", "legal@digitalocean.com"],
        },
        {
          id: "2",
          provider_name: "AWS",
          ncii_instances: 892,
          type: "Cloud",
          region: "Global",
          active_urls: 156,
          removed_urls: 736,
          takedown_rate: 0.83,
          avg_response_time: 2.8,
          contacts: ["abuse@amazonaws.com", "trust-safety@amazon.com"],
        },
        {
          id: "3",
          provider_name: "Hostinger",
          ncii_instances: 623,
          type: "Shared Hosting",
          region: "Europe",
          active_urls: 112,
          removed_urls: 511,
          takedown_rate: 0.82,
          avg_response_time: 4.1,
          contacts: ["abuse@hostinger.com"],
        },
        {
          id: "4",
          provider_name: "Bluehost",
          ncii_instances: 345,
          type: "Shared Hosting",
          region: "North America",
          active_urls: 67,
          removed_urls: 278,
          takedown_rate: 0.81,
          avg_response_time: 3.5,
          contacts: ["abuse@bluehost.com", "legal@bluehost.com"],
        },
        {
          id: "5",
          provider_name: "Linode",
          ncii_instances: 534,
          type: "Cloud VPS",
          region: "Global",
          active_urls: 98,
          removed_urls: 436,
          takedown_rate: 0.82,
          avg_response_time: 3.0,
          contacts: ["abuse@linode.com"],
        },
        {
          id: "6",
          provider_name: "Vultr",
          ncii_instances: 412,
          type: "Cloud VPS",
          region: "Global",
          active_urls: 78,
          removed_urls: 334,
          takedown_rate: 0.81,
          avg_response_time: 3.3,
          contacts: ["abuse@vultr.com", "support@vultr.com"],
        },
        {
          id: "7",
          provider_name: "Hetzner",
          ncii_instances: 567,
          type: "Dedicated Server",
          region: "Europe",
          active_urls: 102,
          removed_urls: 465,
          takedown_rate: 0.82,
          avg_response_time: 2.9,
          contacts: ["abuse@hetzner.com", "legal@hetzner.de"],
        },
        {
          id: "8",
          provider_name: "OVH",
          ncii_instances: 789,
          type: "Dedicated Server",
          region: "Europe",
          active_urls: 145,
          removed_urls: 644,
          takedown_rate: 0.82,
          avg_response_time: 3.7,
          contacts: ["abuse@ovh.net", "legal@ovhcloud.com"],
        },
        {
          id: "9",
          provider_name: "GoDaddy",
          ncii_instances: 298,
          type: "Shared Hosting",
          region: "Global",
          active_urls: 54,
          removed_urls: 244,
          takedown_rate: 0.82,
          avg_response_time: 3.1,
          contacts: ["abuse@godaddy.com"],
        },
        {
          id: "10",
          provider_name: "Namecheap",
          ncii_instances: 387,
          type: "Shared Hosting",
          region: "Global",
          active_urls: 71,
          removed_urls: 316,
          takedown_rate: 0.82,
          avg_response_time: 3.4,
          contacts: ["abuse@namecheap.com", "legal@namecheap.com"],
        },
      ];
      setProviders(mockProviders);
      setHasMore(false);
      setTotalCount(mockProviders.length); // For mock data, total equals current data length
    } catch (err) {
      // if (err instanceof ApiClientError) {
      //   if (err.status === 401) {
      //     logout();
      //   } else {
      //     setError(err.detail || "Failed to load hosting providers");
      //   }
      // } else {
      //   setError("Failed to load hosting providers");
      // }
      setError("Failed to load hosting providers");
    } finally {
      setLoading(false);
    }
  }, [currentPage, logout]);

  useEffect(() => {
    loadProviders();
  }, [loadProviders]);

  const handleNextPage = () => {
    if (!loading && hasMore) {
      setCurrentPage((prev) => prev + 1);
    }
  };

  const handlePreviousPage = () => {
    if (!loading && currentPage > 1) {
      setCurrentPage((prev) => prev - 1);
    }
  };

  // Define columns for the Hosting Providers table
  const columns: ColumnDef<HostingProvider>[] = [
    {
      key: "provider_name",
      header: "Provider Name",
      render: (item) => (
        <div className="text-xs font-medium text-foreground">
          {item.provider_name}
        </div>
      ),
      sortValue: (item) => item.provider_name,
    },
    {
      key: "ncii_instances",
      header: "NCII Instances",
      render: (item) => (
        <div className="text-xs text-muted-foreground">
          {item.ncii_instances}
        </div>
      ),
      sortValue: (item) => item.ncii_instances,
    },
    {
      key: "type",
      header: "Type",
      render: (item) => (
        <div className="text-xs text-muted-foreground">{item.type}</div>
      ),
      sortValue: (item) => item.type,
    },
    {
      key: "region",
      header: "Region",
      render: (item) => (
        <div className="text-xs text-muted-foreground">{item.region}</div>
      ),
      sortValue: (item) => item.region,
    },
    {
      key: "active_urls",
      header: "Active URLs",
      render: (item) => (
        <div className="text-xs text-muted-foreground">{item.active_urls}</div>
      ),
      sortValue: (item) => item.active_urls,
    },
    {
      key: "removed_urls",
      header: "Removed URLs",
      render: (item) => (
        <div className="text-xs text-muted-foreground">{item.removed_urls}</div>
      ),
      sortValue: (item) => item.removed_urls,
    },
    {
      key: "takedown_rate",
      header: "Takedown Rate",
      render: (item) => (
        <div className="text-xs text-muted-foreground">
          {(item.takedown_rate * 100).toFixed(1)}%
        </div>
      ),
      sortValue: (item) => item.takedown_rate,
    },
    {
      key: "avg_response_time",
      header: "Avg Response Time",
      render: (item) => (
        <div className="text-xs text-muted-foreground">
          {item.avg_response_time.toFixed(1)} days
        </div>
      ),
      sortValue: (item) => item.avg_response_time,
    },
    {
      key: "contacts",
      header: "Contacts",
      render: (item) => (
        <div className="text-xs text-muted-foreground">
          {item.contacts.join(", ")}
        </div>
      ),
      sortValue: (item) => item.contacts.join(", "),
    },
    {
      key: "actions",
      header: "Actions",
      sortable: false,
      render: (item) => (
        <Button
          variant="outline"
          size="sm"
          className="h-7 px-3"
          onClick={() => {
            // TODO: Navigate to provider details or open modal
            console.log("View hosting provider:", item.id);
          }}
        >
          View
        </Button>
      ),
    },
  ];

  // Define filters (for search functionality)
  const filters: FilterOption[] = [
    { label: "Type", placeholder: "All Types" },
    { label: "Region", placeholder: "All Regions" },
  ];

  // Handle new provider creation
  const handleNewProvider = () => {
    // TODO: Implement new provider modal or navigation
    console.log("Add new hosting provider");
  };

  // Handle export
  const handleExport = () => {
    // TODO: Implement export functionality
    console.log("Export hosting providers");
  };

  // Action buttons for the header
  const actionButtons = (
    <>
      <Button
        variant="outline"
        size="sm"
        onClick={handleExport}
        className="h-9 px-4"
      >
        <Download className="h-4 w-4 mr-2" />
        Export
      </Button>
      <Button
        size="sm"
        onClick={handleNewProvider}
        className="h-9 px-4"
      >
        <Plus className="h-4 w-4 mr-2" />
        Add Provider
      </Button>
    </>
  );

  return (
    <DataTable
      title="Hosting Providers"
      subtitle="Track hosting providers and takedown effectiveness"
      columns={columns}
      data={providers}
      loading={loading}
      error={error}
      filters={filters}
      searchPlaceholder="Search hosting providers..."
      onNextPage={handleNextPage}
      onPreviousPage={handlePreviousPage}
      hasNextPage={hasMore}
      hasPreviousPage={currentPage > 1}
      currentPage={currentPage}
      itemsPerPage={PAGE_SIZE}
      actionButtons={actionButtons}
      totalCount={undefined}
      entityName="hosting providers"
    />
  );
}



